<template>
  <v-progress-linear
    v-if="!loaded"
    indeterminate
  />
  <div
    v-else
    class="d-flex flex-column ga-8"
    data-testid="dashboard-v2"
  >
    <div class="d-flex flex-column ga-5">
      <DashboardIntro
        @add-application="router.push({ name: 'ChildSelectOrCreate' })"
        @create-new-form="newFamilyFormDialog.open()"
        :disable-new-application="disableNewApplication"
        :first-name="store.state.profile?.first_name"
        :has-requestable-forms="store.getters.hasFamilyRequestableFormSchemas"
        :hub-experience="hubExperience"
      />

      <DashboardInstructions :schema="groupSchema" />
    </div>

    <div class="d-flex flex-column ga-16">
      <DashboardTasks
        @reload-tasks="loadSummariesAndTasks"
        :tasks="applicationTasks"
      />

      <DashboardForms v-if="store.getters.hasFamilyFacingFormSchemas" />

      <DashboardApplicationSummaries
        :items="applicationSummariesExcludingFormsAndWaitListRequests"
      />

      <DashboardFamilySubsidyPrograms
        v-if="!hubExperience"
        @create="createFamilySubsidy"
        :family-subsidy-programs="filteredFamilySubsidyPrograms"
        :processing="creatingSubsidy"
      />

      <EligibilityScreenerResult
        v-if="hubExperience"
        dashboard-view
      />

      <NewFamilyFormDialog
        ref="newFamilyFormDialog"
        :group-id="group?.id"
      />
    </div>
  </div>
</template>

<script setup>
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import DashboardApplicationSummaries from '@/parent/components/dashboards/DashboardApplicationSummaries.vue';
import DashboardFamilySubsidyPrograms from '@/parent/components/dashboards/DashboardFamilySubsidyPrograms.vue';
import DashboardForms from '@/parent/components/dashboards/DashboardForms.vue';
import DashboardInstructions from '@/parent/components/dashboards/DashboardInstructions.vue';
import DashboardIntro from '@/parent/components/dashboards/DashboardIntro.vue';
import DashboardTasks from '@/parent/components/dashboards/DashboardTasks.vue';
import EligibilityScreenerResult from '@/parent/views/eligibility-screener/EligibilityScreenerResult.vue';
import NewFamilyFormDialog from '@/parent/components/form/NewFamilyFormDialog.vue';
import { subsidyProgramIsOpen } from '@/parent/services/subsidies';
import useApi from '@/shared/composables/useApi';

const { api: Api } = useApi();
const router = useRouter();
const store = useStore();

const applicationSummaries = ref([]);
const creatingSubsidy = ref(false);
const familySubsidyProgramChildSubsidyProgramIds = ref([]);
const familySubsidyPrograms = ref([]);
const hubExperience = ref(false);
const loaded = ref(false);
const newFamilyFormDialog = ref(null);
const subsidyPrograms = ref([]);

defineProps({
  group: {
    type: Object,
    default: null,
  },
  groupSchema: {
    type: Object,
    default: null,
  },
});

// Our initial implementation of new enrollment forms should only show in-progress forms and only in the To Do section
const applicationSummariesExcludingFormsAndWaitListRequests = computed(() => {
  return applicationSummaries.value.filter((item) => !item.form_name && !item.wait_list_offer);
});

const applicationTasks = computed(() => {
  return applicationSummaries.value.filter((item) => ![null, 'Review'].includes(item.action));
});

const disableNewApplication = computed(() => {
  const hasChildSubsidies = subsidyPrograms.value.length > 0;

  if (!hasChildSubsidies) return true;

  const allProgramsManaged = subsidyPrograms.value.every((subsidyProgram) =>
    familySubsidyProgramChildSubsidyProgramIds.value.includes(subsidyProgram.id),
  );

  return allProgramsManaged;
});

const filteredFamilySubsidyPrograms = computed(() => {
  return familySubsidyPrograms.value.filter(displayFamilySubsidyProgram);
});

function displayFamilySubsidyProgram(familySubsidyProgram) {
  const hasApplication = hasApplicationSummary(familySubsidyProgram);
  const hashApplicationInProgress = hasApplicationSummaryInProgress(familySubsidyProgram);
  const hasMultipleApplicationsEnabled =
    familySubsidyProgram.enable_multiple_applications &&
    (!hasApplication || !hashApplicationInProgress);

  return (
    subsidyProgramIsOpen(familySubsidyProgram) &&
    !familySubsidyProgram.enable_invite_only &&
    (hasMultipleApplicationsEnabled || !hasApplication)
  );
}

function getFamilySubsidyProgramApplications(familySubsidyProgram) {
  return applicationSummaries.value.filter(
    (application) => application.family_subsidy_program_id === familySubsidyProgram.id,
  );
}

function hasApplicationSummary(familySubsidyProgram) {
  return getFamilySubsidyProgramApplications(familySubsidyProgram).length > 0;
}

function hasApplicationSummaryInProgress(familySubsidyProgram) {
  return (
    getFamilySubsidyProgramApplications(familySubsidyProgram).filter(
      (application) => !application.submitted_at,
    ).length > 0
  );
}

async function load() {
  loaded.value = false;

  await loadSummariesAndTasks();

  const resp = await Api.publicApi.organization.family_subsidy_program.index();
  familySubsidyPrograms.value = resp.data.map((familySubsidyProgram) => {
    if (familySubsidyProgram.subsidy_program_id) {
      familySubsidyProgramChildSubsidyProgramIds.value.push(
        familySubsidyProgram.subsidy_program_id,
      );
    }
    return {
      ...familySubsidyProgram,
      has_applied: Boolean(hasApplicationSummary(familySubsidyProgram)),
    };
  });

  const subsidyResp = await Api.shared.subsidyProgram.index();
  subsidyPrograms.value = subsidyResp.data;

  applicationSummaries.value = applicationSummaries.value.filter(shouldShowInviteOnlyFamilySubsidy);

  await setHubExperience();

  loaded.value = true;
}

async function loadSummariesAndTasks() {
  const response = await Api.parent.applicationSummary.index();
  applicationSummaries.value = response?.data;
}

async function setHubExperience() {
  const response = await Api.screenerProgram.index();
  const screenerPrograms = response?.data;

  hubExperience.value = screenerPrograms && screenerPrograms.length > 0;
}

const shouldShowInviteOnlyFamilySubsidy = (item) => {
  if (item.application_type !== 'FamilySubsidy') return true;

  const familySubsidyProgram = familySubsidyPrograms.value.find(
    (familySubsidyProgram) => familySubsidyProgram.id === item.family_subsidy_program_id,
  );

  if (!familySubsidyProgram?.enable_invite_only) return true;

  return !item.expired || item.submitted_at;
};

async function createFamilySubsidy(familySubsidyProgram) {
  creatingSubsidy.value = true;
  try {
    const resp = await Api.parent.family_subsidy.create({
      family_subsidy_program_id: familySubsidyProgram.id,
    });
    await router.push({ name: 'FamilySubsidyContinue', params: { id: resp.data.id } });
  } finally {
    creatingSubsidy.value = false;
  }
}

onMounted(load);
</script>
